<template>
  <div class="scripts-wrapper">
    <div class="scripts-header">
      <h4>Customize your script:</h4>
      <Button label="Need help?" @click="showHelpModal" class="p-button-link" />
      <button
        @click="showAiModal"
        class="generate-ai-button"
      >
       <img class="generate-with-ai-img" src="../../../assets/images/generate-with-ai.png" />  Generate with AI
      </button>
    </div>

    <ScriptsHelpModal v-model:visible="helpModalVisible" />
    <GenerateWithAIModal
      :campaign="campaignDetail"
      :aiForm="aiForm"
      v-model:visible="generateWithAiModalVisible"
    />

    <div class="text">What do you want the creators to say and show?</div>

    <div class="scripts" ref="scriptWrapper">

      <template v-if="scripts.length">
        <draggable
          v-model="scripts"
          handle=".script-drag"
          @start="onDragStart"
          @end="onDragEnd"
          item-key="id">
          <template #item="{element: script, index}">
            <div>
              <Script
                :script="scripts[index]"
                :index="index"
                :hide-new-button="isDragStarted"
                @update:script="onScriptUpdate(index)"
                @duplicate="onScriptDuplicate"
                @delete="onScriptDelete"
              />

              <button class="add-new-script" :class="{hovered: canShowFirstAddButton && index === 0}">
                <div class="tooltip" v-if="index === 0 && canShowTooltip">
                  <div class="tooltip-title">New scene adding</div>
                  <div class="tooltip-text">Hover the area between the blocks to add a new scene.</div>
                  <Button label="Got It!" class="p-button-sm" @click.stop="hideTooltip" />
                </div>

                <span class="add-new-script-content" @click="addNewScript(index)">
                  <span class="add-new-script-icon">
                    <i class="pi pi-plus"></i>
                  </span>
                </span>

              </button>
            </div>
          </template>
        </draggable>
      </template>

      <template v-else>
        <NoData>
          <template #title>You don't have any scene</template>
          <template #text>
            Please add a scene script for the creator so <br /> they can create a video ad for you.
            <div class="p-mt-4">
              <Button label="Add scene" icon="pi pi-plus" class="p-button-md" @click="addNewScript(-1)" />
            </div>
          </template>
        </NoData>
      </template>
    </div>
  </div>
  <Footer>
    <Button label="Previous step" class="p-button-text p-button-secondary" icon="pi pi-angle-left" @click.prevent="emit('onBack')" />
    <Button label="Next Step" class="p-ml-auto next-button p-button-md p-button-footer" @click.prevent="onNextStep" :disabled="!canNextStep" />
  </Footer>
</template>

<script setup>
import draggable from 'vuedraggable'
import Footer from "./Footer";
import {useAdTemplateScript} from "../../../hooks/useAdTemplateScript";
import {ref, toRef, watch} from "vue";
import Script from "./Script";
import NoData from "../../NoData";
import ScriptsHelpModal from "./ScriptsHelpModal";
import GenerateWithAIModal from './GenerateWithAIModal.vue';

const emit = defineEmits(['onBack', 'onNext', 'update:campaign']);
const props = defineProps({
  campaign: {type: Object, required: true}
});

const {getTemplateScripts, scriptIsFullFilled} = useAdTemplateScript();

const helpModalVisible = ref(false);
const generateWithAiModalVisible = ref(false);
const canShowTooltip = ref(true);
const canShowFirstAddButton = ref(true);
const isDragStarted = ref(false);
const scripts = toRef(props.campaign, 'scripts');
const campaignDetail = toRef(props, 'campaign');
const canNextStep = ref(false);

const aiForm = ref({
    selected_brand_tone: null,
    objective: null,
    adNeeds: '',
});

const isSavedCampaignTemplateChanged = scripts.value[0]?.template?.id && props.campaign.ad_template_id !== scripts.value[0]?.template?.id;

if (props.campaign.ad_template_id && (!scripts.value.length || isSavedCampaignTemplateChanged)) {
  const loadedScripts = (await getTemplateScripts(props.campaign.ad_template_id)).data;

  scripts.value = loadedScripts.map((script, index) => {
    script.ad_template_script_id = script.id;
    script.position = index;
    delete script.id;

    return script;
  });
}

const onDragStart = () => isDragStarted.value = true;
const onDragEnd = () => {
  isDragStarted.value = false;
  scripts.value.forEach((script, index) => script.position = index);
};

const addNewScript = index => {
  const position = index + 1;
  const script = {
    position: position,
    need_to_say: '',
    need_to_show: '',
    title: '',
  }

  scripts.value.forEach(script => {
    if (script.position >= position) {
      script.position++;
    }
  });

  scripts.value.splice(position, 0, script);
  scripts.value = scripts.value.map((script, index) => ({
    ...script,
    position: index
  }))

}

const onScriptUpdate = (index, scriptData) => {
  // console.log(index, scriptData);
  // const script = scripts.value.find((script, scriptIndex) => scriptIndex === index);
  // Object.assign(script, scriptData);
}

watch(() => scripts.value, () => {
  canNextStep.value = scripts.value.length && scripts.value.every(scriptIsFullFilled);
}, {immediate: true, deep: true})

const onNextStep = () => emit('onNext');
const onScriptDuplicate = (script, index) => scripts.value.splice(index + 1, 0, {...script});
const onScriptDelete = index => scripts.value.splice(index, 1);
const hideTooltip = () => {
  canShowTooltip.value = false;
  canShowFirstAddButton.value = false;
};
const showHelpModal = () => helpModalVisible.value = true
const showAiModal = () => generateWithAiModalVisible.value = true

</script>

<style lang="scss" scoped>
@import "../../../assets/sass/theme_variables";

.scripts-wrapper {
  width: toRem(548);
  margin: 0 auto;
  padding-bottom: 1rem;
}

.scripts-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: toRem(16);

  h4 {
    margin-bottom: 0;
  }

  .p-button {
    font-weight: 600;
    text-decoration: underline;

    &:hover, .p-button-label {
      text-decoration: none;
    }
  }
}

.text {
  margin-bottom: toRem(24);
}

.generate-ai-button {
  text-decoration: none !important;
  background: linear-gradient(108deg, #47cdd4, #ed2afc);
  border-radius: 50px;
  color: white;
  padding: 8px 17px;
  border: none;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
}

.generate-ai-button:hover {
  text-decoration: none !important;
  background: linear-gradient(108deg, #47cdd4, #ed2afc);
  border-radius: 50px;
  color: white;
  padding: 8px 17px;
  border: none;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
}

.generate-with-ai-img {
  width: 20px;
}

.add-new-script {
  height: toRem(16);
  position: relative;
  width: 100%;
  padding-top: toRem(16);
  border: none;
  outline: none;
  color: $bgBlocks;
  background: none;
  transition: height  0.3s, padding-top  0.3s, padding-bottom  0.3s;

  &:hover, &.hovered {
    height: toRem(64);
    padding-bottom: toRem(16);

    .add-new-script-content {
      height: 32px;
      background-color: rgba(15, 139, 253, 0.05);
      border-color: $primaryColor;
    }

    .add-new-script-icon {
      display: flex;
    }

  }

  .add-new-script-icon {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: toRem(10);
    position: absolute;
    width: toRem(24);
    height: toRem(24);
    border-radius: 50%;
    left: 50%;
    top: 3px;
    transform: translateX(-50%);
    background: $textColor;
  }

  .tooltip {
    left: 100%;
    top: 50%;
    transform: translate(18px, -50%);
  }
}

.add-new-script-content {
  cursor: pointer;
  height: 0;
  display: block;
  position: relative;
  border: 1px solid transparent;
  border-radius: $borderRadius;
  transition: border-color 0.3s, background-color 0.3s, height  0.3s;
  overflow: hidden;

  &:before, &:after {
    position: absolute;
    top: 14px;
    height: 2px;
    background: $textColor;
    border-radius: 2px;
    content: '';
  }

  &:before {
    left: toRem(12);
    right: 50%;
    margin-right: 16px;
  }

  &:after {
    right: toRem(12);
    left: 50%;
    margin-left: 16px;
  }
}

.tooltip {
  min-width: 260px;
  text-align: left;
  position: absolute;
  padding: toRem(16);
  background: $textColor;
  color: $bgBlocks;
  border-radius: 8px;
  z-index: 10;
  font-size: toRem(16);

  &:before {
    position: absolute;
    left: 0;
    top: 50%;
    width: 0;
    height: 0;
    border-top: 10px solid transparent;
    border-right: 10px solid $textColor;
    border-bottom: 10px solid transparent;
    transform: translate(-100%, -50%);
    content: '';
  }

  .p-button {
    margin-top: toRem(16);
  }
}

.tooltip-title {
  font-weight: bold;
  color: $bgBlocks;
}

.tooltip-text {
  opacity: 0.8;
  line-height: 140%;
}
</style>
